"use client";

import { FormEvent, KeyboardEvent as ReactKeyboardEvent, memo, useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { chat, createConversation, getConversationTurns, listConversations } from "@/lib/api";
import { ConversationSummary, ConversationTurn } from "@/lib/types";
import { useAuth } from "@/contexts/auth-context";
import { ResponseRenderer } from "@/components/chat/response-renderer";

const ConversationList = memo(function ConversationList({
  conversations,
  selectedConversationId,
  onSelectConversation,
  isLoading,
}: {
  conversations: ConversationSummary[];
  selectedConversationId: string | null;
  onSelectConversation: (id: string) => void;
  isLoading: boolean;
}) {
  return (
    <div className="flex-1 space-y-2 overflow-y-auto p-3">
      {isLoading ? (
        <p className="px-2 py-1 text-sm text-slate-500">Loading conversations...</p>
      ) : conversations.length === 0 ? (
        <p className="px-2 py-1 text-sm text-slate-500">No conversations yet.</p>
      ) : (
        conversations.map((conversation) => (
          <button
            key={conversation.conversation_id}
            onClick={() => onSelectConversation(conversation.conversation_id)}
            className={`w-full rounded-lg px-3 py-2 text-left text-sm ${
              selectedConversationId === conversation.conversation_id
                ? "bg-slate-100 text-slate-900"
                : "text-slate-700 hover:bg-slate-50"
            }`}
          >
            <p className="truncate font-medium">{conversation.title}</p>
            <p className="truncate text-xs text-slate-500">{conversation.last_user_message ?? "No messages yet"}</p>
          </button>
        ))
      )}
    </div>
  );
});

const TurnsList = memo(function TurnsList({
  turns,
  isLoading,
  threadEndRef,
}: {
  turns: ConversationTurn[];
  isLoading: boolean;
  threadEndRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div className="flex-1 overflow-y-auto bg-slate-50 px-3 py-4 md:px-6">
      {isLoading ? (
        <p className="text-sm text-slate-500">Loading conversation...</p>
      ) : turns.length === 0 ? (
        <div className="mx-auto mt-8 max-w-2xl rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
          Start a prompt to begin this conversation.
        </div>
      ) : (
        <div className="space-y-5">
          {turns.map((turn) => (
            <div key={turn.turn_id} className="space-y-3">
              <div className="flex justify-end">
                <article className="max-w-3xl rounded-2xl bg-slate-900 px-4 py-3 text-sm text-white shadow-sm">
                  {turn.user_message}
                </article>
              </div>

              <div className="flex justify-start">
                <div className="w-full max-w-[1100px] rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                  {turn.error ? (
                    <article className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {turn.error}
                    </article>
                  ) : null}

                  {turn.response ? <ResponseRenderer response={turn.response} /> : null}
                </div>
              </div>
            </div>
          ))}
          <div ref={threadEndRef} />
        </div>
      )}
    </div>
  );
});

export default function ChatPage() {
  const router = useRouter();
  const { token, user, isAuthenticated, isLoadingUser, clearSession } = useAuth();
  const [message, setMessage] = useState("");
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [turns, setTurns] = useState<ConversationTurn[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingTurns, setIsLoadingTurns] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const handleMessageChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  }, []);

  const handleSelectConversation = useCallback((id: string) => {
    setSelectedConversationId(id);
  }, []);

  const loadConversations = useCallback(async (preferredConversationId?: string) => {
    if (!token) {
      return;
    }

    setIsLoadingConversations(true);
    try {
      const response = await listConversations(token);
      let nextConversations = response.conversations;

      if (nextConversations.length === 0) {
        const created = await createConversation(token, "New conversation");
        nextConversations = [created];
      }

      setConversations(nextConversations);

      if (preferredConversationId && nextConversations.some((item) => item.conversation_id === preferredConversationId)) {
        setSelectedConversationId(preferredConversationId);
      } else if (!selectedConversationId || !nextConversations.some((item) => item.conversation_id === selectedConversationId)) {
        setSelectedConversationId(nextConversations[0]?.conversation_id ?? null);
      }
    } catch {
      setConversations([]);
      setSelectedConversationId(null);
    } finally {
      setIsLoadingConversations(false);
    }
  }, [token, selectedConversationId]);

  const loadTurns = useCallback(async (conversationId: string) => {
    if (!token) {
      return;
    }

    setIsLoadingTurns(true);
    try {
      const response = await getConversationTurns(token, conversationId);
      setTurns(response.turns);
    } catch {
      setTurns([]);
    } finally {
      setIsLoadingTurns(false);
    }
  }, [token]);

  const handleCreateConversation = useCallback(async () => {
    if (!token) {
      return;
    }

    const created = await createConversation(token, "New conversation");
    setConversations((prev) => [created, ...prev]);
    setSelectedConversationId(created.conversation_id);
    setTurns([]);
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      return;
    }
    void loadConversations();
  }, [isAuthenticated, token, loadConversations]);

  useEffect(() => {
    if (!isAuthenticated || !token || !selectedConversationId) {
      return;
    }
    void loadTurns(selectedConversationId);
  }, [isAuthenticated, token, selectedConversationId, loadTurns]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns, isLoadingTurns]);

  if (isLoadingUser) {
    return (
      <main className="mx-auto flex min-h-screen max-w-5xl items-center justify-center px-6 py-16">
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center">
          <p className="text-slate-700">Restoring session...</p>
        </div>
      </main>
    );
  }

  if (!isAuthenticated || !token) {
    return (
      <main className="mx-auto flex min-h-screen max-w-5xl items-center justify-center px-6 py-16">
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center">
          <p className="text-slate-700">You need to login to use chat.</p>
          <button
            onClick={() => router.push("/login")}
            className="mt-3 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
          >
            Go to login
          </button>
        </div>
      </main>
    );
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!message.trim() || !token) {
      return;
    }

    let conversationId = selectedConversationId;
    if (!conversationId) {
      const created = await createConversation(token, "New conversation");
      setConversations((prev) => [created, ...prev]);
      setSelectedConversationId(created.conversation_id);
      conversationId = created.conversation_id;
    }

    const turnId = crypto.randomUUID();
    const userMessage = message.trim();
    setMessage("");
    setIsSending(true);
    setTurns((prev) => [
      ...prev,
      {
        turn_id: turnId,
        user_message: userMessage,
        created_at: new Date().toISOString(),
      },
    ]);

    try {
      const response = await chat({ message: userMessage, conversation_id: conversationId }, token);
      const responseConversationId = response.metadata.conversation_id ?? conversationId;

      setTurns((prev) =>
        prev.map((turn) => (turn.turn_id === turnId ? { ...turn, response } : turn)),
      );

      if (responseConversationId && responseConversationId !== selectedConversationId) {
        setSelectedConversationId(responseConversationId);
      }

      await loadConversations(responseConversationId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Chat failed";
      setTurns((prev) =>
        prev.map((turn) => (turn.turn_id === turnId ? { ...turn, error: errorMessage } : turn)),
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleTextareaKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey) {
      return;
    }

    if (event.nativeEvent.isComposing) {
      return;
    }

    event.preventDefault();
    if (!isSending && message.trim()) {
      event.currentTarget.form?.requestSubmit();
    }
  };

  return (
    <main className="mx-auto flex h-screen max-w-7xl gap-4 overflow-hidden px-4 py-4">
      <aside className="hidden w-80 shrink-0 flex-col rounded-2xl border border-slate-200 bg-white md:flex">
        <div className="border-b border-slate-200 p-4">
          <h1 className="text-base font-semibold text-slate-900">Football AI Chat</h1>
          <p className="mt-1 text-xs text-slate-500">{user?.username ?? "unknown"}</p>
          <button
            onClick={() => void handleCreateConversation()}
            className="mt-3 w-full rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white"
          >
            + New chat
          </button>
        </div>

        <ConversationList
          conversations={conversations}
          selectedConversationId={selectedConversationId}
          onSelectConversation={handleSelectConversation}
          isLoading={isLoadingConversations}
        />

        <div className="border-t border-slate-200 p-3">
          <button
            onClick={() => {
              clearSession();
              router.push("/login");
            }}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700"
          >
            Logout
          </button>
        </div>
      </aside>

      <section className="min-w-0 flex-1 rounded-2xl border border-slate-200 bg-white">
        <div className="flex h-full flex-col">
          <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3 md:hidden">
            <h1 className="text-base font-semibold text-slate-900">Football AI Chat</h1>
            <button
              onClick={() => {
                clearSession();
                router.push("/login");
              }}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-700"
            >
              Logout
            </button>
          </header>

          <TurnsList turns={turns} isLoading={isLoadingTurns} threadEndRef={threadEndRef} />

          <div className="border-t border-slate-200 bg-white p-3 md:p-4">
            <form onSubmit={onSubmit} className="mx-auto flex w-full max-w-4xl items-end gap-2">
              <textarea
                value={message}
                onChange={handleMessageChange}
                onKeyDown={handleTextareaKeyDown}
                className="h-24 w-full resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm"
                placeholder="Ask about players, stats, scouting, or comparisons..."
              />
              <button
                type="submit"
                disabled={isSending || !message.trim()}
                className="h-11 rounded-xl bg-slate-900 px-4 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSending ? "Sending..." : "Send"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}
